import React, { useState, useEffect } from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import Logo from "../../assets/logo_jothi.jpg";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import axios from "axios";
import { API_BASE_URL } from "../../config";
// amountToRelease / loanAmount / gst / insuranceAmount store PAISE.
import { toRupees } from "../../utils/money";

// PDF styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: "#FFFFFF",
    fontSize: 10,
  },
  header: {
    marginBottom: 20,
    borderBottom: "1 solid #000000",
    paddingBottom: 10,
    textAlign: "center",
  },
  companyName: {
    fontSize: 18,
    fontWeight: "bold",
  },
  documentTitle: {
    fontSize: 14,
    marginTop: 5,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    backgroundColor: "#EEEEEE",
    padding: 5,
  },
  text: {
    marginBottom: 4.5,
    lineHeight: 0.7,
  },
  bold: {
    fontWeight: "bold",
  },
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
  },
  tableColHeader: {
    width: "40%",
    borderStyle: "solid",
    borderRightWidth: 1,
    borderColor: "#000",
    backgroundColor: "#F0F0F0",
    padding: 5,
    fontSize: 9,
    fontWeight: "bold",
  },
  tableCol: {
    width: "60%",
    padding: 5,
    fontSize: 9,
  },
  signatureBox: {
    marginTop: 20,
    width: "45%",
  },
  signatureLine: {
    width: "100%",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    borderBottomStyle: "solid",
    marginTop: 20,
  },
  footer: {
    marginTop: 20,
    fontSize: 8,
    textAlign: "center",
    color: "#666",
  },
});

// TableRow component for Schedule A
const TableRow = ({ label, value }) => (
  <View style={styles.tableRow}>
    <View style={styles.tableColHeader}>
      <Text>{label}</Text>
    </View>
    <View style={styles.tableCol}>
      <Text>{value || "N/A"}</Text>
    </View>
  </View>
);

// ChequeTableRow component for Schedule B
const ChequeTableRow = ({ index, bankName, chequeNumber }) => (
  <View style={styles.tableRow}>
    <View style={[styles.tableCol, { width: "20%" }]}>
      <Text>{index + 1}</Text>
    </View>
    <View style={[styles.tableCol, { width: "40%" }]}>
      <Text>{bankName || "N/A"}</Text>
    </View>
    <View style={[styles.tableCol, { width: "40%" }]}>
      <Text>{chequeNumber || "N/A"}</Text>
    </View>
  </View>
);

// PDF document component
const LoanAgreementPDF = ({
  personalDetailsData,
  memberDetailsData,
  loanDetailsData,
  proposedLoanDetailsData,
  bankDetailsData,
  otherDetailsData,
  branchDetailsData,
}) => {
  // Format functions
  const formatDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Takes PAISE. Every money field on this document comes from a column
  // converted by 20260909124252-money-to-paise-bigint-stage2 —
  // amountToRelease, loanAmount, gst, gstOnProcessingFee, insuranceAmount and
  // processingFee are all paise now. Converting inside the formatter rather
  // than at each call site means a field added later cannot miss it.
  //
  // monthlyEmi is the exception: int(11), never converted, still rupees. It is
  // passed through formatRupees below instead.
  const formatCurrency = (paise) => {
    if (!paise && paise !== 0) return "N/A";
    return formatRupees(toRupees(paise));
  };

  const formatRupees = (value) => {
    if (!value && value !== 0) return "N/A";
    return `${Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const sanitize = (value) => DOMPurify.sanitize(value || "N/A");

  // Calculate EMI if not provided
  const calculateEMI = (loanAmount, rateOfInterest, tenureInMonths) => {
    if (!loanAmount || !rateOfInterest || !tenureInMonths) return 0;
    const P = Number(loanAmount);
    const r = Number(rateOfInterest) / 100 / 12; // Monthly interest rate
    const n = Number(tenureInMonths);
    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return Math.round(emi);
  };

  // Handle multiple borrowers
  let borrowers = [];
  if (Array.isArray(personalDetailsData) && personalDetailsData.length > 0) {
    borrowers = personalDetailsData.map((b) => ({
      name: b.name,
      age: b.age,
      occupation: otherDetailsData?.profession || b.profession || "",
    }));
  } else if (proposedLoanDetailsData?.applicantName) {
    borrowers = [
      {
        name: proposedLoanDetailsData.applicantName,
        age: personalDetailsData?.age || "N/A",
        occupation: otherDetailsData?.profession || "",
      },
    ];
    if (proposedLoanDetailsData.coApplicant1) {
      borrowers.push({
        name: proposedLoanDetailsData.coApplicant1,
        age: "N/A",
        occupation: "",
      });
    }
  }

  // Schedule A data
  const scheduleA = {
    // "Date of Application": formatDate(proposedLoanDetailsData?.emiDate || ""),
    "Date of Execution of Agreement": formatDate(new Date()),
    // "Place of Execution": sanitize(branchDetailsData?.branchName || "Rameshwaram"),
    // "Branch": sanitize(branchDetailsData?.branchName || ""),
    "Name of Borrower(s)": sanitize(proposedLoanDetailsData?.applicantName) || "N/A",
    "Age": sanitize(personalDetailsData?.age) || "N/A",
    // "Occupation": borrowers.map((b) => sanitize(b.occupation)).join(", ") || sanitize(otherDetailsData?.profession) || "N/A",
    // "Purpose of Loan": sanitize(loanDetailsData?.loanPurpose || ""),
    // Use ?? not ||: amountToRelease is legitimately 0 until an admin approves
    // the loan. With ||, a real 0 fell through to the full loanAmount and this
    // legal document overstated the disbursed figure.
    "Loan Amount Sanctioned": formatCurrency(memberDetailsData?.amountToRelease ?? proposedLoanDetailsData?.loanAmount ?? 0),
    "GST": formatCurrency(memberDetailsData?.gst || proposedLoanDetailsData?.gstOnProcessingFee || 0),
    "Insurance Amount": formatCurrency(memberDetailsData?.insuranceAmount || 0),
    "Net Eligible Disbursement": formatCurrency(memberDetailsData?.amountToRelease ??
      (proposedLoanDetailsData?.loanAmount -
        (memberDetailsData?.gst ?? proposedLoanDetailsData?.gstOnProcessingFee ?? 0) -
        (memberDetailsData?.insuranceAmount ?? 0) -
        (proposedLoanDetailsData?.processingFee ?? 0))),
    "Rate of Interest": proposedLoanDetailsData?.interestRate
      ? `${sanitize(proposedLoanDetailsData.interestRate)}%`
      : (proposedLoanDetailsData?.rateOfInterest
        ? `${sanitize(proposedLoanDetailsData.rateOfInterest)}%`
        : "24%"),
    "Tenure (months)": sanitize(proposedLoanDetailsData?.tenureInMonths || "N/A"),
    // formatRupees, not formatCurrency: monthlyEmi is int(11) and was never
    // converted, and calculateEMI is given a rupee principal — so both sides of
    // the || are already rupees. Running them through formatCurrency would
    // divide a correct figure by 100.
    "EMI Amount": formatRupees(
      proposedLoanDetailsData?.monthlyEmi ||
      calculateEMI(
        toRupees(proposedLoanDetailsData?.loanAmount ?? memberDetailsData?.amountToRelease),
        proposedLoanDetailsData?.interestRate || proposedLoanDetailsData?.rateOfInterest || 24,
        proposedLoanDetailsData?.tenureInMonths
      )
    ),
    "Repayment Frequency": sanitize(proposedLoanDetailsData?.emiDate ? "Monthly" : "N/A"),
    "EMI Date": sanitize(proposedLoanDetailsData?.emiDate ? proposedLoanDetailsData.emiDate + " of each month" : "N/A"),
    "Late Payment Charges": sanitize(proposedLoanDetailsData?.latePaymentCharges || "As per company policy"),
    "Security": sanitize(proposedLoanDetailsData?.security || "Personal Guarantee"),
    "Processing Fee": formatCurrency(proposedLoanDetailsData?.processingFee || 0),
    "Funding Agency": sanitize(proposedLoanDetailsData?.fundingAgency

      || "JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD")
  };

  // Handle cheques
  const cheques = Array.isArray(bankDetailsData)
    ? bankDetailsData.map((b) => ({
      bankName: b.bankName,
      chequeNumber: b.chequeNumber || "N/A",
    }))
    : bankDetailsData?.bankName
      ? [{ bankName: bankDetailsData.bankName, chequeNumber: "N/A" }]
      : [];

  const today = new Date().toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <Document
      title={`Loan Agreement - ${borrowers.map((b) => sanitize(b.name)).join(", ") || "Unknown"}`}
      author="JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={Logo} style={{ width: 150, height: "auto", alignSelf: "center", marginBottom: 10 }} />
          <Text style={styles.companyName}>
            JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD
          </Text>
          <Text style={styles.documentTitle}>LOAN AGREEMENT</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.text}>
            THIS AGREEMENT is executed at place and date as more particularly described in the
            Schedule A BY The Borrower(s), details whereof is/are more particularly described in the
            Schedule A and, wherever the context so requires, includes Co-Borrower(s) also named
            therein and hereinafter collectively (if there is more than one Borrower(s)) referred to
            as the "Borrower(s)" (which expression shall, unless repugnant to the context or meaning
            thereof, be deemed to include his/her heirs, executors, administrators, successors and
            permitted assigns of the ONE PART; AND
          </Text>
          <Text style={[styles.text, styles.bold]}>
            JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD
          </Text>
          <Text style={styles.text}>
            A Former Producer Company established under The Companies Act 2011, having its
            Registered Office at 120-A, Railway Feeder Road, Market Street, Rameshwaram-623 526,
            Ramanathapuram District, Tamil Nadu and one of its branch/Cluster/Regional office amongst
            other places as more particularly described in Schedule A appended to this Agreement
            (hereinafter called "Company" which expression shall, unless it be repugnant to the
            subject or context thereof, include its successors and assigns).
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>WHEREAS</Text>
          <Text style={styles.text}>
            The Borrower has made an application to the Company for granting a loan under Company's
            Scheme viz. "{sanitize(loanDetailsData?.loanCompanyNames || "Micro Enterprise Loan")}" for{" "}
            {sanitize(loanDetailsData?.loanPurpose || "FISHING")}, hereinafter referred to as "Loan", and the
            Company has agreed to grant financial assistance against the creation of such security by
            the Borrower(s) in favor of the Company as the Company may, from time to time, in its
            sole discretion, deem fit, to be repaid in Equated Monthly Installments (EMI) and at the
            rate of interest, processing fees, services charges, Legal and valuation fees, additional
            interest, penalties as elaborated in the Sanction Letter of the Company and Schedule A
            appended to this Agreement.
          </Text>
          <Text style={styles.text}>
            Now, in consideration of the Company granting the aforesaid loan as detailed in the
            Schedule attached hereto, the Borrowers, do hereby agree as follows:
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>1. UNDERTAKING</Text>
          <Text style={styles.text}>
            1.1 The loan will be utilized for the purpose for which it is granted as per the Sanction
            Letter.
          </Text>
          <Text style={styles.text}>
            1.2 The amount of loan will be repaid by way of EMIs and at the rate of interest,
            processing fees, other costs, charges and expenses in the manner provided herein.
          </Text>
          <Text style={styles.text}>
            1.3 The Borrower shall create security and/or arrange for the guarantee(s) as may be
            considered appropriate by the Company in such form and manner as the Company may in its
            sole discretion require as security for the payment of all the dues of the#pragma warning disable CS8604 // Possible null reference argument.
            Borrower under
            this agreement or otherwise in relation to this loan including principal amounts,
            interest, EMI, additional interest, costs, charges and all other amounts due. In addition,
            the Borrower shall make available to the Company such documents and in such form and
            manner as may be considered appropriate by the Company at its sole discretion to
            effectuate creation/perfection of proper security for the loan.
          </Text>
          <Text style={styles.text}>
            1.4 The Borrower shall not sell, charge, lease, surrender, assign, transfer, further
            encumber (registered or unregistered) or otherwise howsoever alienate, transfer, create
            interest in favor of other person or deal with/in the security so created/agreed to be
            created or any part thereof or permit any charge, encumbrance or lien of any kind
            whatsoever to be created over such security during the continuation of the liability of
            the aforesaid loan.
          </Text>
          <Text style={styles.text}>
            1.5 The security so created by the Borrower will, if so required by the Company, be
            insured against loss, fire, earthquake, floods and other damages with an Insurance Trust,
            approved by the Company.
          </Text>
          <Text style={styles.text}>
            1.6 The relevant municipal/house/land taxes, dues to the govt. and all kinds of
            legal/statutory dues connected with such security would be paid in time and nothing shall
            be done which will affect the security cover for the loan.
          </Text>
          <Text style={styles.text}>
            1.7 All security furnished by the Borrower to the Company shall remain a continuing
            security to the Company binding upon the Borrower and shall:
          </Text>
          <Text style={styles.text}>
            • not be discharged by any intermediate payment by the Borrower or any partial settlement
            of accounts or by limitation due to expiry of any other security document;
          </Text>
          <Text style={styles.text}>
            • be in addition to and not in derogation of any other security which the Company may at
            any time hold in respect of the Borrower's dues;
          </Text>
          <Text style={styles.text}>
            • be available to the Company until all accounts between the Company the Borrower are
            ultimately settled.
          </Text>
          <Text style={styles.text}>
            1.8 The following documents will be submitted by the Borrower to the Company:
          </Text>
          <Text style={styles.text}>
            • All receipts for payment of dues to the Educational/Vocational Training institutions and
            related expenses;
          </Text>
          <Text style={styles.text}>
            • Such additional documents and in such form and manner and within such times as may be
            prescribed by the Company from time to time;
          </Text>
          <Text style={styles.text}>
            1.9 The Borrower shall ensure that the payment of the interest due on the loan shall be
            free from any deduction whether on account of tax or otherwise. Should any applicable law
            at any time require any deduction or withholding to be made from a payment, his liability
            in respect of the payment shall be for a revised amount such that after making of such
            deduction or withholding the net payment shall be equal to the amount which the Company
            would have received, had no such deduction or withholding been made. The Borrower further
            confirms that any withholding tax obligation, in respect of the interest payable on the
            loan whether under the Indian Law or the Laws of any other country where the Borrower may
            reside, would be his responsibility.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>2. CONDITIONS FOR DISBURSEMENT</Text>
          <Text style={styles.text}>
            2.1 The Company may disburse the amount directly to the Educational institution, as the
            case may be, or as requested/specified by the Borrower to the Company at the time of
            disbursement. The Company shall not be responsible/liable in any manner whatsoever for
            any delay by the Borrower in providing such request/specification to the Company and the
            Borrower shall not claim any costs, charges and expenses in relation to any non-disbursal
            by the Company due to any such delay by the Borrower.
          </Text>
          <Text style={styles.text}>
            2.2 The Company would be free to stop disbursements of further installments on the
            happening of any of the events which will affect the security created/to be created in any
            way i.e. due to stoppage of Education, litigation, or for any other reason of whatsoever
            nature.
          </Text>
        </View>
      </Page>

      <Page size="A4" style={styles.page}>
        {/* <View style={styles.header}>
          <Text style={styles.companyName}>
            JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD
          </Text>
          <Text style={styles.documentTitle}>LOAN AGREEMENT (Continued)</Text>
        </View> */}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>3. INTEREST AND CHARGES</Text>
          <Text style={styles.text}>
            3.1 The Borrower shall pay to the Company interest at the rate stipulated by the Company
            from time to time at its sole discretion notwithstanding the ruling rate of interest
            appearing in the Schedule attached hereto. The interest on the loan shall accrue on a
            daily basis and shall be calculated on monthly basis, at the rate as set out in the
            Schedule hereunder.
          </Text>
          <Text style={styles.text}>
            3.2 The Borrower will be charged a rate of interest as mentioned in the terms and
            conditions. The fixed rate of interest will be valid for period of 1 year where after it
            may be changed based on review. The change of interest rate will be displayed the
            Company's web site and such display will be deemed to be due notice of change to the
            borrower/s. The Company shall have the sole discretion to determine such changes in the
            rate of interest.
          </Text>
          <Text style={styles.text}>
            3.3 The Borrower shall pay all types of taxes, charges, duties, processing fees, stamp
            duties, and penalties, if any, registration charges, etc., as are payable in connection
            with the loan and the security created or to be created in connection with the loan
            availed of by the Borrower from the Company.
          </Text>
          <Text style={styles.text}>
            3.4 The Borrower will tender post-dated/undated cheques, if required by the Company and
            the same will be used at the sole discretion of the Company for recovery of amounts due
            from time to time.
          </Text>
          <Text style={styles.text}>
            3.5 The Borrower may give suitable standing instructions/NACH/ECS instructions to debit
            his/their Saving/Current account to recover the EMIs and the said standing instructions
            shall be irrevocable during the currency of the loan.
          </Text>
          <Text style={styles.text}>
            3.6 The Borrower will ensure that the margin amount, that is his own contribution for the
            Education Fee/Livelihood Activity/Micro-enterprise activity, would be paid before availing
            of the loan and its disbursements, evidenced by suitable receipts.
          </Text>
          <Text style={styles.text}>
            3.7 The Borrower will further furnish at his/their own cost and expenses, details of the
            pre-disbursal and post-disbursal documents as required by the Company for the loan
            granted/to be granted.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>4. INSURANCE</Text>
          <Text style={styles.text}>
            4.1 The borrower requests (as evidenced in the application form) to avail insurance on
            the life/lives as mentioned in Schedule A.
          </Text>
          <Text style={styles.text}>
            4.2 The borrower(s) hereby authorize the Lender to pay the insurance premium on
            his/their behalf towards the insurance provider as mentioned in Schedule A and such paid
            amount shall be included in total amount disbursed towards Loan as agreed and mentioned in
            the Loan Agreement.
          </Text>
          <Text style={styles.text}>
            4.3 The borrower understands that the Lender shall be sole beneficiary of the Master
            Policy for any claim out of the same and the Lender shall be entitled to avail the claim
            amount directly from the Insurer without any separate letter/authority from borrower(s) at
            the time of making such claim.
          </Text>
          <Text style={styles.text}>
            4.4 The borrower understands that the Lender shall be entitled to provide any documents
            and information to the Insurer, collected by the Lender during processing aforesaid Loan.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>5. UNDERTAKING FOR UDC/PDC</Text>
          <Text style={styles.text}>
            The Borrower has agreed to furnish undated/post-dated cheques for collection purpose as
            mentioned in Schedule B as required by the Lender and Undertakes, declares and
            acknowledges as under:
          </Text>
          <Text style={styles.text}>
            5.1 The Borrower authorises the Lender to complete the aforesaid cheques as mentioned in
            Schedule B and acknowledges that Cheque completed by the Lender would bind the Borrower
            and would have the same effect as if having been completed by the Borrower.
          </Text>
          <Text style={styles.text}>
            5.2 The Borrower understands and acknowledges that dishonour of the Cheques mentioned in
            Schedule B would attract provisions of Sec 138 of Negotiable Instruments Act, 1881.
          </Text>
          <Text style={styles.text}>
            5.3 The Borrower acknowledges that Sec 20 of Negotiable Instruments Act, 1881 would
            apply to the aforesaid cheques.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>6. GENERAL</Text>
          <Text style={styles.text}>
            6.1 The Company shall be entitled:
          </Text>
          <Text style={styles.text}>
            • To recall the entire outstanding of the loan disbursed at any time at its own
            discretion, without prejudice to any other rights, if it has reason to believe that the
            loan was procured by improper declaration/information or the security offered is
            inadequate or affected in any way due to non-payment of dues, improper maintenance or for
            any other reason of whatsoever nature.
          </Text>
          <Text style={styles.text}>
            • To outsource the services of any agent for the purpose of procuring the loan
            applications, processing the loan requests, make detailed studies of the financial papers,
            disburse the loan, complete the legal requirements, if any, for and in relation to the
            creation of security including representing the Borrower before regulatory authorities,
            Co-Operative Housing Society or elsewhere and appoint collection agents for recovering all
            the dues from the Borrower.
          </Text>
          <Text style={styles.text}>
            • To assign the outstanding debt arising out of this loan partly or fully, along with the
            underlying security, in favor of one or more persons/entities on such terms and
            conditions at the Company's discretion, without seeking any consent from the Borrower.
          </Text>
          <Text style={styles.text}>
            6.2 The Company may at its sole discretion make available, or discontinue the provision
            of alerts and other information through electronic media, whether by way of short
            messaging services ("SMS"), email or other such means, ("Electronic Information") to the
            Borrower in connection with his/her loan account. The Borrower agrees that in case any
            such facilities are provided to the Borrower, the Company's then applicable rules and
            regulations, by whatsoever name called, applicable for such facilities shall be deemed to
            be incorporated herein. The Borrower accepts that the Electronic Information may contain
            certain information relating to the Borrower and his/her loan account and hereby
            authorizes the Company to send such information, even if the Borrower does not
            specifically request for the same, if the Company deems that it is relevant. The Borrower
            acknowledges that the Company may use the services of third party service providers, such
            as cellular service providers for the purposes of sending the Electronic Information, and
            accordingly authorizes the Company to make available information relating to the Borrower
            and his/her loan account to such third party service providers. The Borrower acknowledges
            that the facility as mentioned above, is dependent on the services of the third party
            service providers and that the Company neither makes any representation nor provides any
            warranty with respect to the quality of the service provided by such third party service
            providers nor does it guarantee the timely delivery, accuracy or security of the contents
            of the Electronic Information. The Company shall not be liable for any non-delivery,
            delays, errors, loss, distortions, interception or misuse of the information by
            unauthorized persons, during or after the transmission of the Electronic Information to
            the Borrower and shall not be liable for any non-performance by the third party service
            providers. The Borrower acknowledges and understands that the Electronic Information
            will be provided only in India and in case of SMS alerts only to the subscribers of
            certain select cellular service providers at certain select regions. The Borrower
            understands and acknowledges that any miscommunication or failure in receipt of any
            Electronic Information shall not be deemed to be a waiver or modification of the
            obligations of the Borrower as per the terms and conditions of this Agreement. The
            Borrower shall independently verify the authenticity and accuracy of the contents of any
            Electronic Information from the Company.
          </Text>
          <Text style={styles.text}>
            6.3 The Borrower hereby expressly gives consent to the Company to disclose any
            information, at any point of time, relating to conduct and operations of the account to
            any person or entity as may be deemed necessary by Company, the Reserve Bank of India
            and/or any other statutory/regulatory authority(ies) and/or any credit information
            companies and/or any other agencies appointed by the Company and/or investors/lenders of
            the Company. The Borrower further agrees that Reserve Bank of India and/or such
            authority(ies) or credit information companies or agencies or investors/lenders of the
            Company can compile such data and/or information and can convey/supply such data and/or
            information and/or results thereof to Government, Reserve Bank of India, other banks,
            and/or financial institutions for any reasons whatsoever. The Borrower expressly waives
            his/her right and discharge the Company and/or Reserve Bank of India and/or any other
            authority appointed by Reserve Bank of India from any liability for disclosure and/or use
            of such information on account of breach of any secrecy clause.
          </Text>
          <Text style={styles.text}>
            6.4 The parties hereto expressly agree that all disputes arising out of and/or relating
            to this agreement including any collateral document may, at the sole discretion of the
            Company, be referred to arbitration in accordance with the provisions of the Arbitration
            and Conciliation Act, 1996 as may be amended, or its re-enactment, by a sole arbitrator,
            appointed by the Company. The costs of such arbitration shall be borne by the losing
            Party or otherwise as determined by the Arbitration Award. If a party is required to
            enforce an Arbitral Award by legal action of any kind, the party against whom such legal
            action is taken shall pay all reasonable costs and expenses and attorney's fees,
            including any cost of additional litigation or arbitration taken by the party seeking to
            enforce the award. The Company may at its sole discretion institute civil suit or criminal
            action against borrower/s, in case of default or wilful non repayment of dues or
            intention to defraud the Company and all related costs will be debited to loan account of
            the borrower/s.
          </Text>
          <Text style={styles.text}>
            6.5 The Borrower/s express, recognize and accept that Jothilinga Fisherman Development
            Producer Company Limited shall, without prejudice to its right to perform such collection
            activities itself through its employees, shall be entitled and have full power to appoint
            one or more third parties to collect pre-EMI/EMI from borrowers and other amounts due by
            this agreement.
          </Text>
          <Text style={styles.text}>
            6.6 Even if a clause/part of this agreement is declared to be invalid by court of law,
            this agreement will survive and continue to be valid for the rest of the terms and
            conditions.
          </Text>
          <Text style={styles.text}>
            6.7 In case any ambiguity/dispute arises on account of interpretation of any clause of
            this Agreement in version other than English, English version of this Agreement will
            prevail.
          </Text>
          <Text style={styles.text}>
            6.8 The lender may assign the loan account to any other Financial Institution, in
            accordance with the guidelines and regulations issued by the Reserve Bank of India in
            that regard, and all the dues payable to the lender thereafter shall be paid to the
            purchasing Financial Institution and the consent of the Borrowers in that regard is
            hereby accorded.
          </Text>
        </View>

        {borrowers.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.text}>
              IN WITNESS WHEREOF the Borrower hereunto has set his/ her hand on the date mentioned above. SIGNED AND DELIVERED BY
            </Text>

            {[...Array(2)].map((_, index) => (
              <View
                key={index}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginTop: 20, // Add top space
                }}
              >
                <Text style={{ marginRight: 5 }}>{index + 1}.</Text>
                <View
                  style={{
                    borderBottomWidth: 1,
                    borderBottomColor: '#000',
                    width: 200,
                  }}
                />
              </View>
            ))}
          </View>
        )}




        <View style={styles.section}>
          <Text style={styles.sectionTitle}>SCHEDULE A - IMPORTANT TERMS OF SANCTION</Text>
          <View style={styles.table}>
            {Object.entries(scheduleA).map(([label, value], index) => (
              <TableRow key={index} label={label} value={value} />
            ))}
          </View>
        </View>

        {/* <View style={styles.section}>
          <Text style={styles.sectionTitle}>SCHEDULE B - UDCs/PDCs RECEIVED FROM THE BORROWER</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={[styles.tableColHeader, { width: "20%" }]}>
                <Text>Sr. No.</Text>
              </View>
              <View style={[styles.tableColHeader, { width: "40%" }]}>
                <Text>Name of Bank</Text>
              </View>
              <View style={[styles.tableColHeader, { width: "40%" }]}>
                <Text>Cheque Number</Text>
              </View>
            </View>
            {cheques.length > 0 ? (
              cheques.map((cheque, index) => (
                <ChequeTableRow
                  key={index}
                  index={index}
                  bankName={sanitize(cheque.bankName)}
                  chequeNumber={sanitize(cheque.chequeNumber)}
                />
              ))
            ) : (
              <View style={styles.tableRow}>
                <View style={[styles.tableCol, { width: "100%" }]}>
                  <Text>No cheques provided</Text>
                </View>
              </View>
            )}
          </View>
        </View> */}

        <View style={styles.footer}>
          <Text>Document generated on: {new Date().toLocaleDateString()}</Text>
        </View>
      </Page>
    </Document>
  );
};

// Main component
const LoanAgreementView = ({
  personalDetailsData = [],
  memberDetailsData = {},
  loanDetailsData = {},
  proposedLoanDetailsData = {},
  bankDetailsData = {},
  branchDetailsData = {},
  otherDetailsData = {},
  customerId,
  loanId,
  onBack,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Local state to store fetched data
  const [pData, setPData] = useState(personalDetailsData);
  const [mData, setMData] = useState(memberDetailsData);
  const [lData, setLData] = useState(loanDetailsData);
  const [plData, setPLData] = useState(proposedLoanDetailsData);
  const [bData, setBData] = useState(bankDetailsData);
  const [oData, setOData] = useState(otherDetailsData);
  const [bDData, setBDData] = useState(branchDetailsData);

  // Fetch data if not provided via props
  const fetchData = async () => {
    try {
      setLoading(true);
      const fetchedData = {};

      // Determine if we should fetch data - if customerId or loanId is provided but data is incomplete
      const shouldFetch = (customerId || loanId) &&
        (!personalDetailsData?.length && !proposedLoanDetailsData?.applicantName);

      if (shouldFetch) {
        console.log("Fetching loan agreement data...");

        // Fetch personal details
        if (!personalDetailsData?.length) {
          const personalResponse = await axios.get(`${API_BASE_URL}/api/getPersonalDetails/${customerId}`);
          if (personalResponse.data) {
            fetchedData.personalDetailsData = personalResponse.data;
            setPData(personalResponse.data);
          }
        }

        // Fetch member details
        if (!memberDetailsData?.customerId) {
          const memberResponse = await axios.get(`${API_BASE_URL}/api/getMemberDetails/${customerId}`);
          if (memberResponse.data) {
            fetchedData.memberDetailsData = memberResponse.data;
            setMData(memberResponse.data);
          }
        }

        // Fetch loan details
        if (!loanDetailsData?.loanPurpose && loanId) {
          const loanResponse = await axios.get(`${API_BASE_URL}/api/getLoanDetails/${loanId}`);
          if (loanResponse.data) {
            fetchedData.loanDetailsData = loanResponse.data;
            setLData(loanResponse.data);
          }
        }

        // Fetch proposed loan details
        if (!proposedLoanDetailsData?.loanAmount && loanId) {
          const proposedResponse = await axios.get(`${API_BASE_URL}/api/getProposedLoanDetails/${loanId}`);
          if (proposedResponse.data) {
            fetchedData.proposedLoanDetailsData = proposedResponse.data;
            setPLData(proposedResponse.data);
          }
        }

        // Fetch bank details
        if ((!Array.isArray(bankDetailsData) || bankDetailsData.length === 0) && customerId) {
          const bankResponse = await axios.get(`${API_BASE_URL}/api/getBankDetails/${customerId}`);
          if (bankResponse.data) {
            fetchedData.bankDetailsData = bankResponse.data;
            setBData(bankResponse.data);
          }
        }

        // Fetch branch details
        if ((!Array.isArray(branchDetailsData) || branchDetailsData.length === 0) && customerId) {
          const branchResponse = await axios.get(`${API_BASE_URL}/api/getBranchDetails/${customerId}`);
          if (branchResponse.data) {
            fetchedData.branchDetailsData = branchResponse.data;
            setBDData(branchResponse.data);
          }
        }

        console.log("Data fetched successfully:", fetchedData);
      } else {
        console.log("Using provided data from props");
      }

      setIsReady(true);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching loan agreement data:", err);
      setError("Failed to fetch loan agreement data. Please try again.");
      setLoading(false);
      // Still set ready to true to allow fallback to provided props
      setIsReady(true);
    }
  };

  useEffect(() => {
    // Log props for debugging
    console.log("LoanAgreementView props:", {
      personalDetailsData,
      memberDetailsData,
      loanDetailsData,
      proposedLoanDetailsData,
      bankDetailsData,
      otherDetailsData,
      customerId,
      loanId
    });

    // Check if critical data is available in props
    const hasData = personalDetailsData?.length || proposedLoanDetailsData?.applicantName;

    if (hasData) {
      // If data already available in props, just set ready
      setPData(personalDetailsData);
      setMData(memberDetailsData);
      setLData(loanDetailsData);
      setPLData(proposedLoanDetailsData);
      setBData(bankDetailsData);
      setOData(otherDetailsData);
      setIsReady(true);
      setLoading(false);
    } else {
      // Otherwise attempt to fetch data
      fetchData();
    }
  }, []);

  if (loading) {
    return (
      <div style={{ padding: "20px", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
        <div style={{ marginBottom: "10px", fontSize: "18px" }}>Loading loan agreement...</div>
        <div style={{ width: "40px", height: "40px", border: "4px solid #f3f3f3", borderTop: "4px solid #3498db", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px", color: "#d32f2f" }}>
        <h3>Error</h3>
        <p>{error}</p>
        <button
          onClick={onBack}
          style={{ padding: "8px 16px", backgroundColor: "#1976d2", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
        >
          Go Back
        </button>
      </div>
    );
  }

  // Get actual data to use (either from props or fetched)
  const actualPersonalData = Array.isArray(pData) && pData.length ? pData : personalDetailsData;
  const actualMemberData = Object.keys(mData).length ? mData : memberDetailsData;
  const actualLoanData = Object.keys(lData).length ? lData : loanDetailsData;
  const actualProposedLoanData = Object.keys(plData).length ? plData : proposedLoanDetailsData;
  const actualBankData = (Array.isArray(bData) && bData.length) || (!Array.isArray(bData) && Object.keys(bData).length)
    ? bData : bankDetailsData;
  const actualOtherData = Object.keys(oData).length ? oData : otherDetailsData;

  // Warn about missing data
  const borrowers = Array.isArray(actualPersonalData) ? actualPersonalData : [];
  const missingData = [
    !borrowers.length && !actualProposedLoanData?.applicantName ? "Borrower details" : null,
    !actualProposedLoanData?.loanAmount ? "Loan amount" : null,
    !actualMemberData?.gst && !actualProposedLoanData?.gstOnProcessingFee ? "GST" : null,
    !actualProposedLoanData?.tenureInMonths ? "Loan tenure" : null,
    !actualProposedLoanData?.rateOfInterest ? "Interest rate" : null,
  ].filter(Boolean);

  if (missingData.length > 0) {
    console.warn("LoanAgreementView: Missing required fields", { missingData });
  }

  return (
    <div className="print-view-container">
      <div className="control-buttons">
        <button
          onClick={onBack}
          className="back-button"
          aria-label="Go back to loan application form"
        >
          Back
        </button>
        <PDFDownloadLink
          document={
            <LoanAgreementPDF
              personalDetailsData={actualPersonalData}
              memberDetailsData={actualMemberData}
              loanDetailsData={actualLoanData}
              proposedLoanDetailsData={actualProposedLoanData}
              bankDetailsData={actualBankData}
              otherDetailsData={actualOtherData}
            />
          }
          fileName={`Loan_Agreement_${borrowers.map((b) => b.name || proposedLoanDetailsData?.applicantName || "Unknown").join("_")}.pdf`}
        >
          {({ loading }) => (
            <button
              className="print-button"
              disabled={loading}
              aria-label="Download loan agreement PDF"
            >
              {loading ? "Generating PDF..." : "Download PDF"}
            </button>
          )}
        </PDFDownloadLink>
      </div>

      <PDFViewer width="100%" height="600px">
        <LoanAgreementPDF
          personalDetailsData={actualPersonalData}
          memberDetailsData={actualMemberData}
          loanDetailsData={actualLoanData}
          proposedLoanDetailsData={actualProposedLoanData}
          bankDetailsData={actualBankData}
          otherDetailsData={actualOtherData}
        />
      </PDFViewer>

      {/* {missingData.length > 0 ? (
        <div style={{ padding: "10px", color: "orange", fontWeight: "bold", marginTop: "10px", backgroundColor: "#FFF9E0", border: "1px solid #FFD700", borderRadius: "4px" }}>
          Warning: Missing data ({missingData.join(", ")}). The PDF may contain placeholder values.
        </div>
      ) : null} */}

      <style jsx>{`
        .print-view-container {
          font-family: "Arial", sans-serif;
          padding: 20px;
          color: #333;
        }
        .control-buttons {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
        }
        .back-button,
        .print-button {
          padding: 10px 16px;
          background-color: #1976d2;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
          transition: background-color 0.3s;
        }
        .print-button {
          background-color: #2e7d32;
        }
        .print-button:disabled {
          background-color: #cccccc;
          cursor: not-allowed;
        }
        .back-button:hover {
          background-color: #1565c0;
        }
        .print-button:hover:not(:disabled) {
          background-color: #1b5e20;
        }
      `}</style>
    </div>
  );
};

LoanAgreementView.propTypes = {
  personalDetailsData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      profession: PropTypes.string,
    })
  ),
  memberDetailsData: PropTypes.shape({
    customerId: PropTypes.string,
    gst: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    insuranceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amountToRelease: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  loanDetailsData: PropTypes.shape({
    loanCompanyNames: PropTypes.string,
    loanPurpose: PropTypes.string,
  }),
  proposedLoanDetailsData: PropTypes.shape({
    applicantName: PropTypes.string,
    coApplicant1: PropTypes.string,
    emiDate: PropTypes.string,
    applicationDate: PropTypes.string,
    executionDate: PropTypes.string,
    executionPlace: PropTypes.string,
    branchOffice: PropTypes.string,
    loanAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rateOfInterest: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tenureInMonths: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    monthlyEmi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    processingFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    gstOnProcessingFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    moratorium: PropTypes.string,
    insuranceTakenFor: PropTypes.string,
    latePaymentCharges: PropTypes.string,
    security: PropTypes.string,
    fundingAgencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  bankDetailsData: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        bankName: PropTypes.string,
        chequeNumber: PropTypes.string,
      })
    ),
    PropTypes.shape({
      bankName: PropTypes.string,
      chequeNumber: PropTypes.string,
    }),
  ]),
  otherDetailsData: PropTypes.shape({
    profession: PropTypes.string,
  }),
  customerId: PropTypes.string,
  loanId: PropTypes.string,
  onBack: PropTypes.func.isRequired,
};

export default LoanAgreementView;